import { i18n } from './i18n.js';
import { mountMoonlightChip } from './ui/moonlight-chip.js';
import { accountOnHoldCopy, explainAccountOnHoldOnce } from './ui/service-pause.js';
import { mountStardustChip } from './ui/stardust-chip.js';
import { closeMoonlightCard, onKeyboardFocus, openMoonlightCard, openStardustTip } from './ui/moonlight-card.js';
import { MoonfallQaEntry } from './qa-entry.js';
import { MoonfallAttribution } from './attribution.js';
import { MoonfallConsent } from './analytics/consent-state.js';
import { MoonfallAnalytics } from './analytics/posthog-init.js';
import { ArrivalCeremony } from './ui/arrival-ceremony.js';
import { BootVeil } from './ui/boot-veil.js';
import { AuthWaitBar } from './ui/auth-wait-bar.js';
import { holdAccountFigures, holdVeilForLobby, whenLobbyArrived } from './ui/lobby-arrival.js';
import { showAchievementBanner } from './ui/banner.js';
import { MoonfallGuest } from './guest-session.js';
import {
    setAccountActions,
    setCloudAchievementBannerConsumer,
    setSignInPresenter,
} from './app-services.js';
import { MoonfallCloud } from './convex-client.js';
import { installConvexTokenRefresh } from './auth-token-sync.js';
/**
 * Main Application Entry Point
 * Initializes all modules and sets up global event listeners
 */

import { AccountMenu } from './ui/account-menu.js';
import { AvatarPicker } from './ui/avatar-picker.js';
import { isPlusPress, pulsePlus } from './ui/chip-plus.js';
import { ConsentGate } from './ui/consent-gate.js';
import { EmailVerificationGate } from './ui/email-verification-gate.js';
import { Effects } from './ui/effects.js';
import { Firebase } from './firebase-loader.js';
import { startExchangeListingArrival } from './ui/exchange-listing-arrival.js';
import { InviteTicket } from './ui/invite-ticket.js';
import { VoucherRedeem } from './ui/voucher-redeem.js';
import { VoucherTicket } from './ui/voucher-ticket.js';
import { MoonfallVoucherLink } from './voucher-link.js';
import { Lobby } from './ui/lobby.js';
import { MatchLoading } from './ui/match-loading.js';
import { Collection } from './ui/collection.js';
import { Platform } from './ui/platform.js';
import { Quests } from './ui/quests.js';
import { Referrals } from './ui/referrals.js';
import { DiscordPings } from './ui/discord-pings.js';
import { SpellAnnounce } from './ui/spell-announce.js';
import { Stardust } from './ui/stardust.js';
import { InviteWelcome } from './ui/invite-welcome.js';
import { StarterReward } from './ui/starter-reward.js';
import { Store } from './ui/store.js';
import { UsernameOnboarding } from './ui/username-onboarding.js';
import { SocketHandler } from './socket-handler.js';
import { GameState } from './game-state.js';

/* The mounted lobby Stardust chip; re-mounted on every lobby entry so a
   sign-out/sign-in never stacks listeners. */
let stardustChip = null;

/**
 * A translation, or the English fallback when it is not resolved.
 *
 * `t()` answers with the KEY when a translation is missing or the file has not
 * loaded yet, and a key is truthy — so the older `t(...) || fallback` shape put
 * `game.processingAction` in front of the player. Treat key-returned as
 * unresolved, which is what the rest of the client already does.
 */
function resolvedText(key, fallback, replacements) {
    const translated = i18n?.t?.(key, replacements);
    return translated && translated !== key ? translated : fallback;
}

/* `const`, so this can only ever be mutated. `app-services.ts` receives this
   exact object at module scope and `ui/account-menu.js` reads it from there;
   reassigning the binding later would strand the registry on an empty object
   and silently disable the dropdown's Sign Out. */
const Account = {};

// itch.io embed context (served at /itch, or framed by an itch host). The game
// already uses the absolute backend `socketUrl` for cross-origin fetches; this
// flag lets any surface adapt if needed.
window.MOONFALL_ITCH = (typeof window !== 'undefined') && (
  window.location.pathname.replace(/\/+$/, '') === '/itch' ||
  /(^|\.)itch\.(io|zone)$/i.test((() => { try { return new URL(document.referrer).hostname; } catch (_) { return ''; } })())
);

let globalEventListenersBound = false;

// The achievement banner implementation lives in scripts/ui/banner.js
// (UI.Banner), which defines showAchievementBanner. This file keeps
// only the cloud-consumption orchestration below.

function sanitizeDirectAchievementImageUrl(value) {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    if (!trimmed || trimmed.startsWith('/api/card-image/')) return undefined;
    if (
        trimmed.startsWith('http') ||
        trimmed.startsWith('data:') ||
        trimmed.startsWith('blob:') ||
        trimmed.startsWith('/') ||
        trimmed.startsWith('/assets/') ||
        trimmed.startsWith('assets/')
    ) {
        return trimmed;
    }
    return undefined;
}

const consumeAndShowCloudAchievementBanners = async function consumeAndShowCloudAchievementBanners(options = {}) {
    if (typeof showAchievementBanner !== 'function' || !MoonfallCloud?.users) {
        return 0;
    }

    const initialDelayMs = Number.isFinite(Number(options.initialDelayMs)) ? Number(options.initialDelayMs) : 0;
    let shownCount = 0;
    let bannerDelayMs = initialDelayMs;

    const queueBanner = (title, subtitle, imageUrl, imageKind, bannerOptions) => {
        const show = () => (bannerOptions
            ? showAchievementBanner(title, subtitle, imageUrl, imageKind, bannerOptions)
            : showAchievementBanner(title, subtitle, imageUrl, imageKind));
        if (bannerDelayMs > 0) setTimeout(show, bannerDelayMs);
        else show();
        // Banner lifetime (4000ms + 300ms slide-out) fits inside this gap so
        // consecutive banners in the single fixed top slot never stack.
        bannerDelayMs += 4500;
        shownCount += 1;
    };

    try {
        let newUnlocks = [];
        if (typeof MoonfallCloud.users.consumeNewCardbackUnlocks === 'function') {
            const response = await MoonfallCloud.users.consumeNewCardbackUnlocks();
            if (Array.isArray(response?.newUnlocks)) {
                newUnlocks = response.newUnlocks;
            }
        }

        if (newUnlocks.length > 0 && MoonfallCloud?.cardbacks) {
            const cardbacks = await MoonfallCloud.cardbacks.list();
            newUnlocks.forEach((slug) => {
                const cb = cardbacks.find((item) => item.slug === slug);
                if (!cb) return;
                queueBanner(
                    `🎴 ${resolvedText('collection.achievements.cardbackUnlocked', 'Cardback Unlocked!', {})}`,
                    cb.name,
                    cb.imageUrl,
                    'cardback',
                    { cardbackSlug: slug },
                );
            });
        }
    } catch (e) {
        console.warn('[Main] Failed to check cardback unlock achievements:', e);
    }

    try {
        let newAchievements = [];
        if (typeof MoonfallCloud.users.consumeNewRecipeDiscoveryAchievements === 'function') {
            const response = await MoonfallCloud.users.consumeNewRecipeDiscoveryAchievements();
            if (Array.isArray(response?.newAchievements)) newAchievements = response.newAchievements;
        }
        const resolvedAchievements = newAchievements.map((achievement) => {
            const cardId = typeof achievement?.cardId === 'string' ? achievement.cardId : '';
            const cardName = achievement?.cardName || cardId || resolvedText('collection.achievements.unknownCard', 'Unknown Card', {});
            const title = resolvedText('collection.achievements.recipeDiscovery', 'Recipe Discovered!', {});
            const subtitle = resolvedText(
                'collection.achievements.recipeDiscoverySubtitle',
                `${cardName} is now craftable from Collection.`,
                { cardName },
            );
            return {
                cardId,
                title,
                subtitle,
                imageUrl: sanitizeDirectAchievementImageUrl(achievement?.imageUrl),
            };
        });
        resolvedAchievements.forEach(({ cardId, title, subtitle, imageUrl }) => {
            queueBanner(title, subtitle, imageUrl, 'card', cardId ? { cardId } : undefined);
        });
    } catch (e) {
        console.warn('[Main] Failed to check recipe-discovery achievements:', e);
    }

    try {
        let newAchievements = [];
        if (typeof MoonfallCloud.users.consumeNewFirstDiscoveryAchievements === 'function') {
            const response = await MoonfallCloud.users.consumeNewFirstDiscoveryAchievements();
            if (Array.isArray(response?.newAchievements)) {
                newAchievements = response.newAchievements;
            }
        }

        const resolvedAchievements = newAchievements.map((achievement) => {
            const cardId = typeof achievement?.cardId === 'string' ? achievement.cardId : '';
            const cardName = achievement?.cardName || cardId || resolvedText('collection.achievements.unknownCard', 'Unknown Card', {});
            const discovererName = achievement?.discovererName || 'Unknown';
            const subtitle = resolvedText(
                'collection.achievements.firstDiscoverySubtitle',
                `${cardName} was first discovered by ${discovererName}. Your name is now recorded as this card's discoverer.`,
                { cardName, discovererName },
            );
            return {
                cardId,
                subtitle,
                imageUrl: sanitizeDirectAchievementImageUrl(achievement?.imageUrl),
            };
        });

        resolvedAchievements.forEach(({ cardId, subtitle, imageUrl }) => {
            queueBanner(
                `🌙 ${resolvedText('collection.achievements.firstDiscovery', 'First Discovery!', {})}`,
                subtitle,
                imageUrl,
                'card',
                cardId ? { cardId } : undefined,
            );
        });
    } catch (e) {
        console.warn('[Main] Failed to check first-discovery achievements:', e);
    }

    return shownCount;
};

// Shared "stay connected" link for the desktop block screen — the one door an
// unsupported-desktop-browser visitor can still walk through. Same Discord as
// the landing. NB: the overlay fires BEFORE i18n init, so its copy is English
// by necessity.
const MOONFALL_DISCORD_URL = 'https://discord.gg/5tAxsXJB4S';
const BLOCK_MOON_SVG =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" fill="currentColor"/></svg>';

// A violet Discord pill for the desktop block overlay.
function blockDiscordLinkHTML() {
    return '<a class="block-discord" href="' + MOONFALL_DISCORD_URL + '"' +
        ' target="_blank" rel="noopener" aria-label="Join the Moonfall community on Discord">' +
        '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.3 4.4A19.8 19.8 0 0 0 15.4 3l-.3.5a18 18 0 0 1 4.4 1.4c-2-1-4.3-1.6-6.6-1.6s-4.6.6-6.6 1.6A18 18 0 0 1 10.7 3.5L10.4 3A19.8 19.8 0 0 0 5.5 4.4 20.8 20.8 0 0 0 2 18.3a20 20 0 0 0 6 3l.7-1.1a13 13 0 0 1-2-1l.5-.4a14.3 14.3 0 0 0 12.2 0l.5.4a13 13 0 0 1-2 1l.7 1.1a20 20 0 0 0 6-3 20.8 20.8 0 0 0-3.5-13.9zM9 15.5c-1 0-1.8-1-1.8-2.1S8 11.3 9 11.3s1.8 1 1.8 2.1S10 15.5 9 15.5zm6 0c-1 0-1.8-1-1.8-2.1S14 11.3 15 11.3s1.8 1 1.8 2.1S16 15.5 15 15.5z"/></svg>' +
        '<span>Come say hi on Discord</span></a>';
}

// Desktop engine floors for the phase-2 gate. At/above the floor boots;
// below it keeps the hard block (a known engine where updating fixes it).
const MIN_CHROMIUM_MAJOR = 110;
const MIN_FIREFOX_MAJOR = 115;
const MIN_SAFARI_MAJOR = 16;
const MIN_SAFARI_MINOR = 4; // 16.4 exactly is supported

/**
 * Desktop browser tri-state gate (pure UA parsing):
 *   'supported' — Chromium >= 110 (ANY Chrome/NNN UA: Chrome, Edge, Opera,
 *                 Brave — no vendor/token exclusions), Firefox >= 115, or
 *                 Safari >= 16.4 (Version/X.Y + Safari/ token, not Chromium).
 *   'outdated'  — one of those engines below its floor → hard block overlay.
 *   'unknown'   — no recognizable engine token → soft notice, boot anyway.
 * Fails open ('supported'): a detection error must never lock a player out.
 */
function getBrowserSupport() {
    try {
        const ua = navigator.userAgent || '';
        const chromium = ua.match(/Chrome\/(\d+)/i);
        if (chromium) return Number(chromium[1]) >= MIN_CHROMIUM_MAJOR ? 'supported' : 'outdated';
        const firefox = ua.match(/Firefox\/(\d+)/i);
        if (firefox) return Number(firefox[1]) >= MIN_FIREFOX_MAJOR ? 'supported' : 'outdated';
        const safari = /Safari\//i.test(ua) ? ua.match(/Version\/(\d+)(?:\.(\d+))?/i) : null;
        if (safari) {
            const major = Number(safari[1]);
            const minor = Number(safari[2] || 0);
            const meetsFloor = major > MIN_SAFARI_MAJOR
                || (major === MIN_SAFARI_MAJOR && minor >= MIN_SAFARI_MINOR);
            return meetsFloor ? 'supported' : 'outdated';
        }
        return 'unknown';
    } catch (_) {
        return 'supported'; // If detection fails, do not block the user
    }
}

function showBrowserUnsupportedBanner() {
    const app = document.getElementById('app');
    if (app) app.style.display = 'none';

    const overlay = document.createElement('div');
    overlay.id = 'browser-unsupported-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-live', 'polite');
    overlay.innerHTML = `
        <div class="browser-unsupported-card">
            <div class="block-moon">${BLOCK_MOON_SVG}</div>
            <div class="browser-unsupported-title">Your browser needs an update to play Moonfall</div>
            <div class="browser-unsupported-body">
                This version is a little too old for the Moon. Update to the latest Chrome, Firefox, or Safari and we'll keep your seat warm.
            </div>
            ${blockDiscordLinkHTML()}
        </div>
    `;
    document.body.appendChild(overlay);
}

// Soft notice for 'unknown' engines — a dismissible hint, never a wall. The
// dismissal is remembered for the session so it does not nag on every boot.
const BROWSER_NOTICE_DISMISS_KEY = 'moonfallBrowserNoticeDismissed';
function showBrowserSoftNotice() {
    try {
        if (sessionStorage.getItem(BROWSER_NOTICE_DISMISS_KEY) === 'true') return;
    } catch (_) { /* private mode: show it */ }
    if (document.getElementById('browser-soft-notice')) return;

    const notice = document.createElement('div');
    notice.id = 'browser-soft-notice';
    notice.className = 'browser-soft-notice';
    notice.setAttribute('role', 'status');

    // Unlike the hard block (which replaces the app before i18n exists), the
    // soft notice persists inside the fully localized app: the English copy
    // is only a pre-init fallback, and the data-i18n hooks re-label it the
    // moment i18n.init() applies translations (and on every language change).
    const text = document.createElement('span');
    text.className = 'browser-soft-notice-text';
    text.setAttribute('data-i18n', 'browserGate.softNotice');
    text.textContent = 'Moonfall plays best on Chrome, Firefox or Safari.';

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'browser-soft-notice-dismiss';
    dismiss.setAttribute('data-i18n-aria-label', 'browserGate.dismissNotice');
    dismiss.setAttribute('aria-label', 'Dismiss browser notice');
    dismiss.textContent = '✕';
    dismiss.addEventListener('click', () => {
        try { sessionStorage.setItem(BROWSER_NOTICE_DISMISS_KEY, 'true'); } catch (_) { /* private mode */ }
        notice.remove();
    });

    notice.append(text, dismiss);
    document.body.appendChild(notice);
}

/**
 * HAND THE CLOUD CLIENT ITS DEPLOYMENT — whenever its code is here.
 *
 * `ConvexHttpClient` arrives through a separate module (convex-loader.js) that
 * only starts downloading once the page has finished parsing, and the boot
 * below waits three seconds for it before going on without the cloud. That
 * boot is one-shot, and until 2026-09-21 this init lived inside it: a client
 * landing at second four found `_moonfallInitialized` already set and was
 * thrown away, so every cloud call for the rest of the session — the sign-in's
 * consent query first of all — failed with "Convex client not initialized"
 * (Firefox on production, reported as "Consent check request failed"). Now
 * the loader's `convex-ready` reaches this directly, however late it fires.
 *
 * Idempotent, and silent while the client is merely not here yet: the
 * fallback boot already says so, and `MoonfallCloud.init` would only add an
 * error for a bundle that is still in flight. Returns whether the client is
 * ready.
 */
function initializeCloudClient() {
    if (!MoonfallCloud) return false;
    if (MoonfallCloud.isReady?.()) return true;
    const convexUrl = window.CONVEX_URL || null;
    if (!convexUrl || typeof window.ConvexHttpClient === 'undefined') return false;
    const initialized = MoonfallCloud.init(convexUrl);
    if (initialized) {
        console.log('[Main] Convex cloud client initialized');
        window.dispatchEvent(new Event('moonfall:convex-ready'));
    }
    return initialized === true;
}

/* How long a sign-in waits for a cloud client that is still downloading. The
   client bundle is ~21KB compressed; a connection that has not delivered it
   ten seconds after the boot gave up on it is one the player must be told
   about, and the fail-closed path below (alert, sign-out, reload) is what
   tells them and retries the download. */
const CLOUD_CLIENT_WAIT_MS = 10000;

/**
 * IS THE CLOUD CLIENT STILL ON ITS WAY?
 *
 * True only while there is something to wait for: a deployment to talk to, a
 * client that is not initialized, and a loader that has not reported failure
 * (`moonfallConvexLoadFailed`, set by convex-loader.js either way once its
 * import settles). Without a deployment URL there is no cloud to wait for, and
 * after a load failure only a reload can bring one.
 */
function cloudClientPending() {
    if (!window.CONVEX_URL || typeof MoonfallCloud?.isReady !== 'function') return false;
    if (MoonfallCloud.isReady()) return false;
    return window.moonfallConvexLoadFailed !== true;
}

/**
 * Wait for the cloud client, bounded. Resolves true once it is initialized,
 * false when the loader reports failure or the bound runs out — and the
 * caller then makes its cloud call anyway, so the failure that follows is
 * the real, already-handled one rather than a new kind.
 */
function waitForCloudClient(timeoutMs) {
    if (!cloudClientPending()) return Promise.resolve(MoonfallCloud?.isReady?.() === true);
    return new Promise((resolve) => {
        let timer = null;
        const finish = (ready) => {
            clearTimeout(timer);
            window.removeEventListener('moonfall:convex-ready', onReady);
            window.removeEventListener('convex-load-failed', onFailed);
            resolve(ready);
        };
        const onReady = () => finish(true);
        const onFailed = () => finish(false);
        window.addEventListener('moonfall:convex-ready', onReady);
        window.addEventListener('convex-load-failed', onFailed);
        timer = setTimeout(() => finish(MoonfallCloud.isReady() === true), timeoutMs);
    });
}

// Main initialization function
async function initializeApp() {
    if (window._moonfallInitialized) return;
    window._moonfallInitialized = true;

    // Mobile devices always boot — mobile is a supported product surface, and
    // iOS has no non-WebKit engines, so the desktop gate must not apply there.
    // Desktop is tri-state: modern Chromium/Firefox/Safari boot, OUTDATED
    // engines hit the hard block, unknown engines get a soft dismissible
    // notice and boot anyway. The escape hatch skips even the soft notice.
    const isMobileDevice = !!Platform?.isMobileUA();
    const allowUnsupportedBrowser = (function () { try { return localStorage.getItem('allowUnsupportedBrowser') === 'true'; } catch (_) { return false; } })();
    if (!allowUnsupportedBrowser && !isMobileDevice) {
        const support = getBrowserSupport();
        if (support === 'outdated') {
            showBrowserUnsupportedBanner();
            if (window.CLIENT_DEBUG_MODE) console.log('Outdated desktop browser detected – blocking gameplay.');
            return; // Do not initialize the game on outdated desktop engines
        }
        if (support === 'unknown') showBrowserSoftNotice();
    }

    /* The loading veil stays until a real surface announces itself (see
       ui/boot-veil.js). Armed here so that no boot path — including one that
       throws before it reaches a surface — can leave the veil up for good. */
    BootVeil.armBackstop();

    // Initialize internationalization (language support)
    if (i18n && typeof i18n.init === 'function') {
        i18n.init().catch(err => {
            console.error('[i18n] Initialization failed:', err);
        });
    }

    initializeCloudClient();

    startGuestTutorialAtBoot();

    // Check if user is already authenticated
    const existingUser = await checkAuthAndProceed();
    if (!existingUser) {
        // Show auth screen and wait for login
        if (window.CLIENT_DEBUG_MODE) console.log('[Main] Showing auth screen');
        return;
    }

    // User is logged in, proceed to game
    proceedToLobby(existingUser);
}

function closeReferralsForAuthTransition() {
    try {
        Referrals?.close?.({ restoreFocus: false });
    } catch (error) {
        console.error('[Main] Referrals auth teardown failed:', error);
    }
}

/**
 * Check auth state and either proceed to lobby or show Firebase sign-in
 */
async function checkAuthAndProceed() {
    // Initialize Firebase
    if (!Firebase) {
        console.error('[Main] Firebase loader not available');
        showFirebaseSignIn();
        return null;
    }

    // A Firebase ID token lives one hour and is refreshed silently. Subscribe
    // BEFORE init so no refresh can land before we are listening: without this,
    // the Convex client kept the credential it was handed at sign-in, and every
    // client-side Convex read went out unauthenticated for the rest of a long
    // session. It refreshes a live session only — see auth-token-sync.js.
    installConvexTokenRefresh(Firebase, MoonfallCloud);

    // Initialize Firebase with auth state callback
    // This callback fires once immediately with the initial auth state
    // (either the cached logged-in user or null if not logged in)
    const initialized = Firebase.init(async (user) => {
        if (user) {
            /* A signed-in user completely bypasses the guest path — any lingering
               guest session is dropped so gameplay uses the Firebase credential.

               THIS CALLBACK IS NOT ALWAYS THE FIRST ONE. On iOS the SDK can
               report signed-out at boot and restore the real session a beat
               later, and by then the play-first funnel has already asked the
               server for a guest tutorial. `abandonForSignedInUser()` disowns
               that room so its `gameStarted` cannot install over this lobby, and
               reports whether a board had already landed — which only this file
               can act on, because the lobby view is not the guest module's. */
            let abandonedGuestBoard = false;
            try { abandonedGuestBoard = MoonfallGuest?.abandonForSignedInUser?.() === true; } catch (_) {}
            // That start answered nothing: a signed-out answer after this one routes.
            bootStartAwaitsAuthAnswer = false;
            /* Otherwise the lobby's reveal would wait on the tutorial's hold,
               up to its cap, behind a board that is no longer coming. */
            releaseGuestTutorialHold();
            if (abandonedGuestBoard) {
                try { Lobby?.switchToLobbyView?.(); } catch (error) {
                    console.error('[Main] Could not leave an abandoned guest board:', error);
                }
            }
            await proceedToLobby(user);
        } else {
            // Not logged in. Play-first: an anonymous visitor who hasn't already
            // seen/skipped the tutorial boots straight into it (no sign-in wall).
            // Returning anonymous visitors get the sign-in screen with a quiet
            // "replay tutorial" option so they're never re-trapped.
            handleSignedOutBoot();
        }
    });

    if (!initialized) {
        console.error('[Main] Firebase initialization failed');
        showFirebaseSignIn();
        return null;
    }

    // Don't show anything yet - wait for onAuthStateChanged callback
    // The callback will handle showing either the lobby or sign-in screen
    return null;
}

/**
 * Route a signed-out visitor: first-timers auto-start the guest tutorial (no wall);
 * returning anonymous visitors see the sign-in screen with a quiet replay option.
 * Multiplayer invitations take precedence over both paths: the room query remains
 * in the URL while Firebase authenticates, then SocketHandler consumes it after
 * the authenticated lobby has initialized.
 * If the guest feature is unavailable for any reason, we fall back to the plain
 * sign-in screen — never a blank page.
 */
/* A BROWSER THAT HAS SIGNED IN IS NOT AN ANONYMOUS FIRST VISIT.

   Kept in the same place, and read the same way, as the guest module's own
   returning-visitor memory (`moonfall_guest_tutorial_skipped`). Cleared on an
   explicit sign-out so a shared device goes back to the play-first funnel. */
const SIGNED_IN_BEFORE_KEY = 'moonfall_browser_signed_in';

function rememberBrowserHasSignedIn() {
    try { window.localStorage.setItem(SIGNED_IN_BEFORE_KEY, 'true'); } catch (_) { /* private mode */ }
}

function browserHasSignedIn() {
    try { return window.localStorage.getItem(SIGNED_IN_BEFORE_KEY) === 'true'; } catch (_) { return false; }
}

function forgetBrowserHasSignedIn() {
    try { window.localStorage.removeItem(SIGNED_IN_BEFORE_KEY); } catch (_) { /* no-op */ }
}

/* THE FIRST "SIGNED OUT" AT BOOT IS NOT ALWAYS THE LAST WORD.

   On iOS the SDK can report signed-out and restore the real session a beat
   later (measured ~200-800ms). `guest-session.js` now makes the resulting
   tutorial fully abandonable, so the player never sees it — but the server has
   still been asked to build a guest game by then, which costs a generated
   opening and one of that IP's daily guest allowances, on EVERY page load.

   So when this browser is known to have an account, the boot decision waits
   once for the SDK to change its mind. It is a bounded wait on an
   asynchronous auth resolution, not a retry: the loading veil is already up
   and stays up, nothing is started, and when it elapses the routing is exactly
   what it would have been without this. It runs at most once, and only while
   that veil is still on screen — a mid-session sign-out routes immediately. */
const AUTH_SETTLE_GRACE_MS = 2200;
/* How long the boot veil may wait for the guest tutorial's board before it
   shows the page regardless (docs/asset-loading.md: every hold is capped).

   It outlasts the connection the start waits on. That connection gives up
   10 s after it was asked for (socket-handler.js, `ensureConnected`), a
   moment after this hold begins: at 10000 the veil faded onto an empty page
   and the failed start's card arrived after it, and a connection that only
   got in on its second handshake, late in those 10 s, met the same gap
   before its board. So: the 10 s, the room being built behind it, and no
   longer than the veil's own backstop (ui/boot-veil.js). A start that asks
   for the connection late (its pass came back late) gets these 12 s again
   from then (`holdVeilForGuestConnection`). */
const GUEST_TUTORIAL_BOOT_HOLD_MS = 12000;
let signedOutBootGraceUsed = false;
let guestStartedAtBoot = false;
/* The boot start answers the SDK's FIRST word only (handleSignedOutBoot):
   cleared by that answer, whichever it is. */
let bootStartAwaitsAuthAnswer = false;
let guestTutorialHold = null;

function shouldWaitForAuthToSettle() {
    if (signedOutBootGraceUsed) return false;
    if (!browserHasSignedIn()) return false;
    // Only during the initial boot, while the veil still covers the screen.
    return Boolean(document.getElementById('app-loading'));
}

function handleSignedOutBoot() {
    if (shouldWaitForAuthToSettle()) {
        signedOutBootGraceUsed = true;
        setTimeout(() => {
            // The user branch may have taken over in the meantime; if it has,
            // there is nothing to route.
            if (Firebase?.isSignedIn?.()) return;
            handleSignedOutBoot();
        }, AUTH_SETTLE_GRACE_MS);
        return;
    }
    closeReferralsForAuthTransition();
    // Firebase sign-out is an authentication boundary for every Convex call,
    // including queued mutations and per-surface background work.
    MoonfallCloud?.setAuthToken?.(null);
    const roomInvite = getPendingRoomInvite();
    if (roomInvite) {
        console.log('[Main] Multiplayer invitation detected, waiting for sign-in');
        showFirebaseSignIn();
        return;
    }

    const guest = MoonfallGuest;
    if (!guest) {
        console.log('[Main] Guest module unavailable, showing sign-in screen');
        showFirebaseSignIn();
        return;
    }
    /* The start made at boot, before the SDK answered (startGuestTutorialAtBoot)
       is this visit's answer to the SDK's first word, whether it is still on
       its way, playing, or already fell back to the sign-in card: asking
       again would mint a second guest token and build a second room. */
    if (bootStartAwaitsAuthAnswer) {
        bootStartAwaitsAuthAnswer = false;
        return;
    }
    if (guest.hasSkipped()) {
        console.log('[Main] Returning anonymous visitor, showing sign-in with replay option');
        showFirebaseSignIn({ returning: true });
        return;
    }
    /* A LATER signed-out answer in a visit whose tutorial started at boot is
       a login refused after Firebase let the player in, or a session lost
       later in the tab: the sign-in card, live. Routing nothing left a
       refused player on a card whose button was still pressed, and a lost
       session on a lobby with no session and no card (audit of round 16). */
    if (guestStartedAtBoot) {
        showFirebaseSignIn();
        return;
    }
    beginGuestTutorial(guest);
}

function beginGuestTutorial(guest) {
    console.log('[Main] Anonymous first visit, auto-starting guest tutorial');
    setupGlobalEventListeners();
    /* The board arrives after a token mint and a server-built room — 1.2s of
       nothing on production when the veil dropped here. The veil now waits for
       `Lobby.switchToGameView` (or the sign-in card a failed start falls back
       to), capped so a start that never answers still shows the page — and,
       once the board is installed, for the board to be whole
       (BootVeil.arrive). The battle's shared art is known now, so it is
       fetched and decoded while the room is being built (ui/match-loading.js):
       measured before, the first-visit board showed its card backs still
       downloading and its pile canvases arriving ~220 ms late. */
    guestTutorialHold = BootVeil.hold('guest-tutorial', GUEST_TUTORIAL_BOOT_HOLD_MS);
    MatchLoading?.warmChrome?.();
    guest.startOrResumeGuestTutorial();
}

/* A BROWSER THAT HAS NEVER SIGNED IN STARTS ITS TUTORIAL AT BOOT.

   On a phone and in Safari the auth SDK readies Google's sign-in iframe (a
   popup must open inside the tap that asks for it) BEFORE its first
   auth-state answer, and that answer is what asked the server for the guest
   tutorial. Measured 25 September 2026: 1.25 s (WebKit, 4G) to 1.8 s (Chrome,
   4G, 4x CPU) between the app script and the tutorial request on every first
   visit; none on desktop Chrome, which readies the iframe lazily.

   A browser that has never signed in here (SIGNED_IN_BEFORE_KEY) has no
   account this page can know of, so the room is built while the SDK finishes.
   Its signed-out answer then finds the start made (handleSignedOutBoot); a
   session the SDK restores after all is abandoned exactly as the iOS race
   below already is, and its veil hold goes with it. Everything that routes
   elsewhere first — a room invitation, a visitor who skipped the tutorial, a
   browser that has signed in — still waits for the SDK. */
function startGuestTutorialAtBoot() {
    if (!Firebase || browserHasSignedIn() || getPendingRoomInvite()) return false;
    const guest = MoonfallGuest;
    if (!guest || typeof guest.startOrResumeGuestTutorial !== 'function' || guest.hasSkipped()) return false;
    guestStartedAtBoot = true;
    bootStartAwaitsAuthAnswer = true;
    beginGuestTutorial(guest);
    return true;
}

/**
 * The guest start now waits on the connection (guest-session.js: its pass is
 * back, or a reload asks for its seat). The connection may take its whole 10 s
 * from here, so the veil's hold is given its 12 s again from now and the
 * backstop armed at boot waits for it (ui/boot-veil.js, `stretch`). When the
 * start was the first to ask for the connection, the veil used to fade onto an
 * empty page just before the failure card arrived.
 */
function holdVeilForGuestConnection() {
    if (typeof guestTutorialHold !== 'function') return;
    BootVeil.stretch('guest-tutorial', GUEST_TUTORIAL_BOOT_HOLD_MS);
}

/** A signed-in player arrived: the guest tutorial's veil hold is not theirs. */
function releaseGuestTutorialHold() {
    const release = guestTutorialHold;
    guestTutorialHold = null;
    if (typeof release === 'function') release();
}

/**
 * Read only the objective shape needed to distinguish an invitation from an
 * ordinary first visit. Room existence and membership stay server-authoritative
 * after authentication; this helper neither resolves nor rewrites the room id.
 */
function getPendingRoomInvite() {
    try {
        const roomId = new URLSearchParams(window.location.search).get('room');
        return typeof roomId === 'string' && roomId.length > 0 ? roomId : null;
    } catch (_) {
        return null;
    }
}


function tr(key, fallback) {
    const val = i18n && typeof i18n.t === 'function' ? i18n.t(key) : null;
    return (val && val !== key) ? val : fallback;
}

/**
 * Prefix a generic failure with the server's own words, and only those. The
 * server states its reason through ConvexError data (the only channel a
 * production deployment preserves); anything else stays a generic, honest
 * failure rather than a diagnosis we invented.
 */
function withServerReason(error, generic) {
    const serverReason = error?.data?.message;
    return typeof serverReason === 'string' && serverReason
        ? `${serverReason}\n\n${generic}`
        : generic;
}

/** Explain a failed account creation without inventing a diagnosis. */
function accountSyncFailureMessage(error) {
    return withServerReason(error, tr(
        'accountSync.failed',
        'We could not finish setting up your account. Please try signing in again.\n\nIf this persists, contact support@moonfall.io.',
    ));
}

/**
 * Explain a failed consent CHECK without borrowing the wrong subsystem's words.
 * ConsentGate tags the step it failed at: a request that never reached the
 * deployment is a connection failure, and calling it "we could not verify your
 * legal consent" sends the player to look for a legal problem that does not
 * exist — the 2026-08-13 sign-in report. Only a deployment that answered with
 * nothing usable is genuinely about consent, and an unclassified failure keeps
 * the conservative wording.
 */
function consentCheckFailureMessage(error) {
    return withServerReason(error, error?.code === 'consent_request_failed'
        ? tr(
            'consentGate.unreachable',
            'We could not reach Moonfall to finish signing you in. Check your connection and try again.\n\nIf this persists, contact support@moonfall.io.',
        )
        : tr(
            'consentGate.verifyFailed',
            'We could not verify your legal consent. Please refresh and try again.\n\nIf this persists, contact support@moonfall.io.',
        ));
}

/**
 * Sign out after a fail-closed login block, best-effort ON PURPOSE.
 *
 * A rejected sign-out must not escape into proceedToLobby's outer catch, which
 * only logs and then falls through to the code that hides the auth view and
 * opens the lobby — turning the block into access on an account we just refused
 * to admit. The account-creation branch was hardened against this; the two
 * consent branches were not.
 */
async function signOutAfterBlockedLogin() {
    releaseRefusedSignIn();
    // Like the menu's sign-out: no face kept for an account turned away.
    AvatarPicker?.forget?.();
    await Promise.resolve(Firebase.signOut()).catch((signOutErr) => {
        console.error('[Main] Sign-out after a blocked login failed:', signOutErr);
    });
}

/**
 * A refused login leaves nothing of the sign-in it refused holding the screen,
 * before it says why: the accepted consent gate comes down (it waits, opaque
 * and busy, for a hand-over that is not coming, up to its 20 s cap) and the
 * sign-in card's buttons are live again, so the alert sits over the card the
 * player retries from. The signed-out answer that follows shows that card too
 * (handleSignedOutBoot); a refusal does not depend on it (audit of round 16:
 * a frozen gate for the rest of its cap, then a card whose button was still
 * pressed).
 */
function releaseRefusedSignIn() {
    ConsentGate?.release?.();
    setSignInPending(document.getElementById('google-signin-btn'), false);
    setSignInPending(document.getElementById('email-signin-btn'), false);
}

/* A signed-in session whose token read answered null is asked twice more, at
   0.5 s and 1.5 s after that answer (a refresh in flight answers null once),
   then refused: a session with no token never opens the lobby. */
const ID_TOKEN_RETRY_DELAYS_MS = [500, 1000];

async function retryIdToken() {
    for (const delayMs of ID_TOKEN_RETRY_DELAYS_MS) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        const token = await Firebase.getIdToken();
        if (token) return token;
    }
    return null;
}

/**
 * Render the guest-specific extras on the sign-in card: the end-of-tutorial
 * "moment" headline + manga teaser, an optional notice (with a "Try again"
 * when the failed start it reports can be tried again), and a quiet
 * "replay tutorial" link for returning anonymous visitors. Idempotent — it fully
 * resets the card so a normal (signed-out) sign-in screen never inherits guest copy.
 */
function renderGuestAuthExtras(options) {
    const authCard = document.querySelector('#auth-view .auth-card');
    const authTitle = document.querySelector('#auth-view .auth-title');
    if (!authCard) return;

    ['auth-guest-teaser', 'auth-guest-replay', 'auth-guest-notice'].forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.parentNode) el.parentNode.removeChild(el);
    });

    const isMoment = options.guest === true;
    if (authTitle) {
        const titleKey = isMoment ? 'guest.signInMoment.title' : 'auth.signInToPlay';
        authTitle.setAttribute('data-i18n', titleKey);
        authTitle.textContent = isMoment
            ? tr('guest.signInMoment.title', 'Keep your cards. Save your story.')
            : tr('auth.signInToPlay', 'Sign In to Play');
    }

    // Insert guest copy between the title and the Google button, so the value prop
    // reads before the CTA (and its negative top-margin tightens under the title
    // rather than overlapping the button).
    const anchor = authCard.querySelector('.auth-oauth-buttons') || document.getElementById('auth-error');
    const before = (node) => {
        if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(node, anchor);
        else authCard.appendChild(node);
    };

    if (isMoment) {
        const teaser = document.createElement('div');
        teaser.id = 'auth-guest-teaser';
        teaser.className = 'auth-guest-teaser';
        const line1 = document.createElement('p');
        line1.className = 'auth-guest-teaser-line';
        line1.setAttribute('data-i18n', 'guest.signInMoment.subtitle');
        line1.textContent = tr('guest.signInMoment.subtitle', 'Sign in to keep the cards you played with and pick up right where you left off.');
        teaser.appendChild(line1);
        before(teaser);
    }

    if (options.notice) {
        const notice = document.createElement('div');
        notice.id = 'auth-guest-notice';
        notice.className = 'auth-guest-notice';
        const retry = options.retry === true ? buildGuestRetryButton() : null;
        if (retry) {
            /* A FAILED START IS NOT A DEAD END. A first visit whose game
               connection got no answer was left on this card reading "Failed
               to connect." with nothing to press except Google (production,
               13 October 2026: 3 of 17 first visits; a reload connected at
               once). guest-session.js says when trying again can help, and
               the message and the way out of it are one block. */
            notice.classList.add('auth-guest-notice--retry');
            notice.setAttribute('role', 'alert');
            const text = document.createElement('span');
            text.className = 'auth-guest-notice-text';
            text.textContent = options.notice;
            notice.append(text, retry);
        } else {
            notice.textContent = options.notice;
        }
        before(notice);
    }

    // A returning anonymous visitor (skipped/finished before) or the post-tutorial
    // moment both offer a one-click "replay tutorial" so nobody feels trapped.
    if (isMoment || options.returning === true) {
        const replay = document.createElement('button');
        replay.id = 'auth-guest-replay';
        replay.type = 'button';
        replay.className = 'auth-guest-replay';
        replay.setAttribute('data-i18n', 'guest.replayTutorial');
        replay.textContent = tr('guest.replayTutorial', 'Replay the tutorial');
        replay.addEventListener('click', () => {
            if (MoonfallGuest && typeof MoonfallGuest.replayTutorial === 'function') {
                setupGlobalEventListeners();
                // The press is answered by the match loading screen at once;
                // a start that fails comes back to this card, which takes it
                // down again (showFirebaseSignIn).
                MatchLoading?.begin?.();
                MoonfallGuest.replayTutorial();
            }
        });
        authCard.appendChild(replay);
    }
}

/**
 * The card's "Try again" after a guest start that failed without the server
 * saying no (guest-session.js, `showStartFailure`). Null when there is no
 * guest tutorial to start.
 *
 * It asks for the seat a reload was resuming if one is still remembered, and
 * for a fresh tutorial otherwise. Answered like a replay: the match loading
 * screen takes the press at once, and a start that fails again comes back to
 * this card, which takes that screen down and draws a live button.
 */
function buildGuestRetryButton() {
    const guest = MoonfallGuest;
    if (typeof guest?.startOrResumeGuestTutorial !== 'function') return null;
    const retry = document.createElement('button');
    retry.id = 'auth-guest-retry';
    retry.type = 'button';
    retry.className = 'auth-guest-retry';
    retry.setAttribute('data-i18n', 'common.tryAgain');
    retry.textContent = tr('common.tryAgain', 'Try again');
    retry.addEventListener('click', () => {
        // One press, one start; the card's next drawing brings a live button.
        retry.disabled = true;
        setupGlobalEventListeners();
        MatchLoading?.begin?.();
        guest.startOrResumeGuestTutorial();
    });
    return retry;
}

/**
 * The polished end-of-tutorial sign-in moment ("Keep your cards. Save your story.").
 * Reuses the normal sign-in card with guest copy layered on top.
 */
function showGuestSignInMoment(context = {}) {
    if (Lobby?.switchToAuthView) {
        try { Lobby.switchToAuthView(); } catch (_) {}
    }
    showFirebaseSignIn({ guest: true, reason: context.reason });
}

/**
 * The sign-in button answers the press and keeps answering until the next
 * screen replaces the card (the consent gate, the username card, the lobby):
 * after Google's popup closed, the card used to sit unchanged for ~2.5 s with
 * the button still live while the session was set up (measured). The shared
 * pending visual (pending-states.css), so no new copy and no geometry change.
 */
function setSignInPending(button, pending) {
    if (!button) return;
    button.disabled = Boolean(pending);
    button.classList.toggle('btn-pending', Boolean(pending));
    if (pending) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
    /* A sign-in that outlasts the grace also shows the one bar, until the
       next surface takes over or the card is live again (ui/auth-wait-bar.js). */
    if (pending) AuthWaitBar.arm(button);
    else AuthWaitBar.disarm(button);
}

/**
 * Show Firebase sign-in UI and set up event handlers.
 * @param {Object} [options] guest-tutorial extras: { guest, returning, notice, retry }
 */
function showFirebaseSignIn(options = {}) {
    closeReferralsForAuthTransition();
    // The sign-in card is a destination: the loading veil comes down on it,
    // and so does a match loading screen whose tutorial start came back here.
    BootVeil.drop('auth');
    MatchLoading?.cancel?.();

    const authView = document.getElementById('auth-view');
    const loadingSpinner = document.getElementById('auth-loading');
    const googleBtn = document.getElementById('google-signin-btn');
    const emailForm = document.getElementById('email-signin-form');
    const toggleSignup = document.getElementById('toggle-signup');
    const authError = document.getElementById('auth-error');
    const emailBtn = document.getElementById('email-signin-btn');

    if (authView) {
        authView.style.display = 'flex';
    }
    // The card is being shown (again): its buttons are live.
    setSignInPending(googleBtn, false);
    setSignInPending(emailBtn, false);

    if (loadingSpinner) {
        loadingSpinner.style.display = 'none';
    }

    /* An invited player's first frame should be the ticket, not a form. This is
       purely presentational: the referral was already recorded from the `ref`
       parameter server-side, and dismissing the screen leaves the player on
       exactly this sign-in view. It shows once per code per browser and never
       blocks — a failure to build it is a no-op. */
    try { InviteTicket?.maybeShow?.(); } catch (_) { /* never block auth */ }

    renderGuestAuthExtras(options || {});

    let isSignUp = false;

    // Google sign-in
    if (googleBtn) {
        googleBtn.onclick = async () => {
            try {
                authError.textContent = '';
                setSignInPending(googleBtn, true);
                await Firebase.signInWithGoogle();
            } catch (error) {
                setSignInPending(googleBtn, false);
                authError.textContent = tr('auth.googleFailed', 'Google sign-in failed');
            }
        };
    }

    // Email sign-in/sign-up
    if (emailForm) {
        emailForm.onsubmit = async (e) => {
            e.preventDefault();
            const email = /** @type {HTMLInputElement} */ (document.getElementById('auth-email')).value;
            const password = /** @type {HTMLInputElement} */ (document.getElementById('auth-password')).value;
            authError.textContent = '';

            try {
                setSignInPending(emailBtn, true);
                if (isSignUp) {
                    await Firebase.signUpWithEmail(email, password);
                } else {
                    await Firebase.signInWithEmail(email, password);
                }
            } catch (error) {
                setSignInPending(emailBtn, false);
                authError.textContent = tr('auth.authenticationFailed', 'Authentication failed');
            }
        };
    }

    // Toggle sign-in/sign-up
    if (toggleSignup && emailBtn) {
        toggleSignup.onclick = (e) => {
            e.preventDefault();
            isSignUp = !isSignUp;
            emailBtn.textContent = isSignUp
                ? tr('auth.signUp', 'Sign Up')
                : tr('auth.signIn', 'Sign In');
            toggleSignup.textContent = isSignUp
                ? tr('auth.alreadyAccount', 'Already have an account? Sign in')
                : tr('auth.needAccount', "Don't have an account? Sign up");
        };
    }
}

function normalizeDisplayUsername(value) {
    if (typeof value !== 'string') return '';
    const normalized = value.trim().replace(/\s+/g, ' ');
    if (!normalized || normalized.includes('@')) return '';
    return normalized;
}

/**
 * The Convex row is the ONLY source of a display name. Never fall back to
 * firebaseUser.displayName: that is the player's real Google name, and the
 * server deliberately refuses to derive a username from it (see
 * resolveInitialUsername in convex/users.ts) because it would leak publicly
 * through exchange listings and discoverer attributions.
 */
function resolveLobbyUsername(_firebaseUser, convexUser) {
    return normalizeDisplayUsername(convexUser?.username) || 'Player';
}

function getStarterInitializationCacheKey(firebaseUser, convexUser) {
    const userKey = convexUser?._id || firebaseUser?.uid || 'anonymous';
    return `moonfall:starterCardsInitialized:${userKey}`;
}

function hasKnownStarterCardsInitialized(firebaseUser, convexUser) {
    try {
        return window.localStorage.getItem(getStarterInitializationCacheKey(firebaseUser, convexUser)) === 'true';
    } catch (_) {
        return false;
    }
}

function rememberStarterCardsInitialized(firebaseUser, convexUser) {
    try {
        window.localStorage.setItem(getStarterInitializationCacheKey(firebaseUser, convexUser), 'true');
    } catch (_) {
        // Non-critical browser storage failure; Convex remains authoritative.
    }
}

function isAlreadyInitializedResult(result) {
    if (result?.initialized) return true;
    const message = String(result?.message || '').toLowerCase();
    return message.includes('already initialized');
}

/* How long a cover (the consent gate) may wait for the finished lobby before
   it leaves anyway: the lobby's own arrival cap plus the account reads before it. */
const CONSENT_HANDOVER_CAP_MS = 12000;

async function initializeStarterCardsIfNeeded(firebaseUser, convexUser) {
    if (!MoonfallCloud?.users?.initializeCards) return;
    if (hasKnownStarterCardsInitialized(firebaseUser, convexUser)) return;

    const result = await MoonfallCloud.users.initializeCards();
    if (isAlreadyInitializedResult(result)) {
        rememberStarterCardsInitialized(firebaseUser, convexUser);
    }
    if (result?.initialized) {
        console.log('[Main] Initialized', result.cardCount, 'cards for new user');
    }
}

/**
 * Called after successful authentication
 * @param {Object} firebaseUser - User data from Firebase
 */
async function proceedToLobby(firebaseUser) {
    /* The loading veil used to drop HERE, before the token exchange, the
       consent check and the lobby build — so the player watched an empty
       background, then the lobby, then (after a mid-match refresh) the board
       over that. It now comes down at the end of this function, once the
       lobby stands and the socket has claimed any pending resume; the consent
       gate and username onboarding drop it themselves when they appear. */

    /* THE AVATAR'S FACE STARTS WITH THE SIGN-IN. The veil waits for the
       disc's face (holdVeilForLobby below), and the face used to be asked for
       only once `getOrCreate` had named it: on the meter's round 13 it was the
       last thing the veil waited for on every WebKit and every cold boot
       (0.4-1.1 s after the quest and Moonlight reads). The face this browser
       showed this account last time starts here instead, alongside the token,
       the consent check and the account read; a changed face loads as before
       (ui/avatar-picker.js `warm`). */
    void AvatarPicker?.warm?.(firebaseUser?.uid);

    // Track convexUser at function scope so it can be used later
    let convexUser = null;
    let starterCardsReady = null;
    /* Started once: a new account's while its name is chosen, anyone else's
       after the consent below. */
    const startStarterCards = () => {
        if (!starterCardsReady) {
            starterCardsReady = initializeStarterCardsIfNeeded(firebaseUser, convexUser)
                .catch((e) => {
                    console.warn('[Main] Failed to check/initialize cards:', e);
                });
        }
        return starterCardsReady;
    };

    /* FAIL CLOSED: every way out of the account block below that did not
       check consent and read the account row ends here, never at the lobby
       build (a null token or an unexpected throw used to open a "Player"/0
       lobby on an account nobody had checked). */
    if (!MoonfallCloud?.users) {
        console.error('[Main] Cloud client missing — blocking lobby access');
        releaseRefusedSignIn();
        alert(tr('consentGate.verifyFailed', 'We could not verify your legal consent. Please refresh and try again.\n\nIf this persists, contact support@moonfall.io.'));
        await signOutAfterBlockedLogin();
        return;
    }

    // Sync user to Convex (creates user record if first login)
    if (MoonfallCloud) {
        try {
            // Get Firebase ID token for Convex (refreshed below if the player
            // verifies their email on the way in).
            let token = (await Firebase.getIdToken()) || await retryIdToken();
            if (!token) {
                console.error('[Main] Sign-in returned no token — blocking lobby access');
                releaseRefusedSignIn();
                alert(tr('consentGate.unreachable', 'We could not reach Moonfall to finish signing you in. Check your connection and try again.\n\nIf this persists, contact support@moonfall.io.'));
                await signOutAfterBlockedLogin();
                window.location.reload();
                return;
            }
            if (token) {
                // Set auth header for Convex calls
                MoonfallCloud.setAuthToken(token);

                // Everything below needs the cloud client. After a boot that
                // gave up waiting for its bundle it may still be downloading —
                // a Firebase session restores in well under the three seconds
                // that boot allows — and the token above is applied to it the
                // moment it initializes. Wait for it rather than fail its very
                // first call; see initializeCloudClient.
                await waitForCloudClient(CLOUD_CLIENT_WAIT_MS);

                // ============================================================
                // GDPR Consent Gate - fail-closed
                // For new users: show gate BEFORE creating Convex user.
                // For returning users: show gate if ToS/Privacy version outdated.
                // If consent check/record fails, sign out and block lobby access.
                // ============================================================
                let pendingConsents = null;
                if (ConsentGate) {
                    try {
                        pendingConsents = await ConsentGate.checkAndShow();
                    } catch (consentErr) {
                        console.error('[Main] Consent check failed — blocking lobby access:', consentErr);
                        releaseRefusedSignIn();
                        alert(consentCheckFailureMessage(consentErr));
                        await signOutAfterBlockedLogin();
                        window.location.reload();
                        return;
                    }
                }

                // Create/get user in Convex database (safe to call whether or not consents were given).
                // Fail CLOSED: without this row the player has no Stardust, no
                // collection, no decks and no subscription, so a lobby opened
                // anyway would fake a healthy session on top of a total failure.
                try {
                    try {
                        convexUser = await MoonfallCloud.users.getOrCreate();
                    } catch (firstErr) {
                        // A NEW account needs a verified email (owner decision,
                        // 2026-09-26). Wait for the link, then carry on from
                        // here with the consent already given in hand.
                        if (firstErr?.data?.code !== 'email_verification_required') throw firstErr;
                        const outcome = await EmailVerificationGate.waitForVerification({
                            email: firebaseUser?.email || Firebase.getUser?.()?.email || '',
                        });
                        if (outcome !== 'verified') {
                            await signOutAfterBlockedLogin();
                            window.location.reload();
                            return;
                        }
                        token = await Firebase.getIdToken();
                        MoonfallCloud.setAuthToken(token);
                        convexUser = await MoonfallCloud.users.getOrCreate();
                    }
                } catch (syncErr) {
                    console.error('[Main] Account creation failed — blocking lobby access:', syncErr);
                    releaseRefusedSignIn();
                    alert(accountSyncFailureMessage(syncErr));
                    await signOutAfterBlockedLogin();
                    return;
                }

                // Record consents AFTER user exists (so the Convex mutation can find the user row).
                // Settles to null once recorded, or to the failure: it never
                // rejects, so the name card and the check below can both wait on it.
                const consentRecording = pendingConsents
                    ? Promise.resolve(ConsentGate.recordConsents(pendingConsents, token))
                        .then(() => null, (recordErr) => recordErr || new Error('Consent recording failed'))
                    : null;

                // New accounts: let the player pick their display name. The default
                // is a random adjective-noun suggestion (never their real Google
                // name — a privacy leak), which they can accept, shuffle, or edit.
                if (convexUser && convexUser.isNewUser && UsernameOnboarding) {
                    /* THE CARD GOES UP AS SOON AS THE ACCOUNT EXISTS. Writing the
                       consent is nothing the card shows (round 15 of the
                       snappiness meter: two of the three round trips in a
                       3.1-5.8 s "Create Account" wait), so it is written behind
                       the card. Nothing past the card goes ahead of it: the name
                       is saved after it (`saveAfter`), the lobby is built after
                       the check below, and a failed write refuses the login at
                       once. The starter cards follow the consent while the name
                       is chosen — 1.3-1.5 s that sat between "Enter Moonfall"
                       and the lobby, in front of its quest rail, since the
                       client's mutations run one at a time. */
                    if (consentRecording) {
                        void consentRecording.then((recordErr) => { if (!recordErr) startStarterCards(); });
                    }
                    try {
                        // The sign-in card has done its work: it must not show
                        // through the name card's backdrop.
                        const authViewBehind = document.getElementById('auth-view');
                        if (authViewBehind) authViewBehind.style.display = 'none';
                        BootVeil.drop('username-onboarding');
                        const saveAfter = consentRecording
                            ? consentRecording.then((recordErr) => { if (recordErr) throw recordErr; })
                            : null;
                        saveAfter?.catch(() => { /* refused below, not by the card */ });
                        /* The card is on screen before the gate leaves it — the
                           handover the consent gate waits for. */
                        const onboarding = UsernameOnboarding.show(convexUser.username, { saveAfter });
                        ConsentGate?.release?.();
                        const chosen = await (consentRecording
                            ? Promise.race([onboarding, consentRecording.then((recordErr) => (recordErr ? null : onboarding))])
                            : onboarding);
                        if (chosen) convexUser.username = chosen;
                    } catch (nameErr) {
                        console.warn('[Main] Username onboarding skipped:', nameErr);
                    }
                }

                if (consentRecording) {
                    const recordErr = await consentRecording;
                    if (recordErr) {
                        console.error('[Main] Consent recording failed — blocking lobby access:', recordErr);
                        releaseRefusedSignIn();
                        alert(tr('consentGate.saveFailed', 'We could not save your legal consent. Please refresh and try again.\n\nIf this persists, contact support@moonfall.io.'));
                        await signOutAfterBlockedLogin();
                        window.location.reload();
                        return;
                    }
                    console.log('[Main] Consent recorded');
                }
                /* A re-consent (returning player) hands the gate over to the
                   finished lobby; after a username card this is a no-op. */
                void whenLobbyArrived({ capMs: CONSENT_HANDOVER_CAP_MS }).then(() => ConsentGate?.release?.());

                // First-party marketing attribution (best-effort, never blocking).
                // Persist captured touches whenever the user has granted marketing
                // consent — at this sign-in (gate opt-in) or previously. The server
                // is authoritative: it set-once protects the first touch and refuses
                // without consent. A fresh decline clears the ephemeral holding area.
                try {
                    const grantedNow = pendingConsents?.marketing?.granted === true;
                    const declinedNow = pendingConsents?.marketing && pendingConsents.marketing.granted === false;
                    const alreadyGranted = convexUser?.marketingConsent?.granted === true;
                    if (MoonfallAttribution) {
                        if (grantedNow || alreadyGranted) {
                            /* Not awaited: it is best-effort by design (commit()
                               swallows its own failures), and awaiting it put a
                               round trip on the boot path of exactly the visitor
                               who arrived from a campaign link. */
                            void MoonfallAttribution.commit();
                        } else if (declinedNow) {
                            MoonfallAttribution.clearPending();
                        }
                    }
                } catch (attrErr) {
                    if (window.CLIENT_DEBUG_MODE) console.warn('[Main] Attribution commit skipped:', attrErr);
                }

                // Analytics: sync consent from the authoritative record (backfills returning
                // users who consented in a past session) and identify the user so server- and
                // client-side events stitch to one person. Best-effort; never blocks login.
                try {
                    const analyticsConsent = pendingConsents?.marketing
                        ? pendingConsents.marketing.granted === true
                        : convexUser?.marketingConsent?.granted === true;
                    if (MoonfallConsent) {
                        MoonfallConsent.setAnalyticsConsent(analyticsConsent);
                    }
                    if (MoonfallAnalytics) {
                        const props = {};
                        const acq = convexUser?.acquisition || {};
                        if (acq.firstTouch?.channel) props.first_touch_channel = acq.firstTouch.channel;
                        if (acq.firstTouch?.source) props.first_touch_source = acq.firstTouch.source;
                        if (acq.firstTouch?.medium) props.first_touch_medium = acq.firstTouch.medium;
                        if (acq.firstTouch?.campaign) props.first_touch_campaign = acq.firstTouch.campaign;
                        if (acq.lastTouch?.channel) props.last_touch_channel = acq.lastTouch.channel;
                        if (convexUser?.subscriptionType) props.subscription_type = convexUser.subscriptionType;
                        const loc = i18n?.getLanguage?.();
                        if (loc) props.locale = loc;
                        const signup = convexUser?.firstSeenDate || convexUser?.createdAt;
                        if (signup) props.signup_date = signup;
                        MoonfallAnalytics.identify(convexUser?._id || convexUser?.id, props);
                    }
                } catch (analyticsErr) {
                    if (window.CLIENT_DEBUG_MODE) console.warn('[Main] Analytics identify skipped:', analyticsErr);
                }

                // Convex initialization is idempotent and uses an indexed existence
                // check, so startup does not need to download the whole inventory.
                // It runs ALONGSIDE the lobby's own reads rather than before them;
                // the boot veil waits for it (holdVeilForLobby below), so no
                // player can open a Collection whose starter cards are still
                // being written.
                startStarterCards();

                // Store user info in game state
                if (GameState) {
                    GameState.setUser({
                        id: convexUser?._id || convexUser?.id,
                        firebaseUid: firebaseUser.uid,
                        username: resolveLobbyUsername(firebaseUser, convexUser),
                        selectedCardback: convexUser?.selectedCardback,
                        unlockedCardbacks: convexUser?.unlockedCardbacks
                    });
                }
            }
        } catch (error) {
            /* Every best-effort step above has its own catch; only a critical
               failure reaches this one. */
            console.error('[Main] Failed to sync user to Convex — blocking lobby access:', error);
            releaseRefusedSignIn();
            alert(accountSyncFailureMessage(error));
            await signOutAfterBlockedLogin();
            return;
        }
    }

    // Hide auth view
    const authView = document.getElementById('auth-view');
    if (authView) {
        authView.style.display = 'none';
    }
    // Its sign-in wait is over: a bar still due in its buttons is not shown.
    AuthWaitBar.disarm(document.getElementById('google-signin-btn'));
    AuthWaitBar.disarm(document.getElementById('email-signin-btn'));
    /* And drop the one-off stacking raise a shared listing link asks for when
       it sends a visitor to sign in. It exists only so that view can be
       reached over the guest tutorial; leaving it set would put `#auth-view`
       above every overlay for the rest of the session. */
    document.body.classList.remove('exchange-signin-open');

    // Show lobby
    const lobbyView = document.getElementById('lobby-view');
    if (lobbyView) {
        lobbyView.style.display = 'block';
        lobbyView.scrollTop = 0;
        // The hub is the active view: the legal bar leaves (hub.css); it is
        // back the moment the sign-in screen is.
        document.body.classList.add('lobby-active');
        Effects?.viewEnter?.(lobbyView);
    }

    /* QUEUED BEHIND THE ARRIVAL SEQUENCE, NOT FIRED ON TOP OF IT.
       This used to run the moment the lobby appeared. That was fine while no
       cardback could be waiting on a brand-new account — but Founder's Legacy
       is granted at signup, so draining here would drop a cardback banner over
       the 500-Stardust screen, the referral payout and the invitation brief.
       For a returning player nothing holds the ceremony, so this still runs on
       arrival; for a new one it runs when the last screen has closed.

       Declared in this file, so the `typeof window.… === 'function'` probe that
       used to guard it is gone with the global: it would now read undefined and
       the banners would silently never show. */
    ArrivalCeremony.queue(() => Promise.resolve(consumeAndShowCloudAchievementBanners())
        .catch((err) => {
            console.warn('[Main] Failed to show cloud achievement banners:', err);
        }));

    // Set up user controls visibility
    const userControls = document.getElementById('user-controls');
    const userIdentityEl = document.getElementById('user-username');
    const stardustEl = document.getElementById('user-stardust');
    const discordLink = document.getElementById('pvp-discord-link');
    const feedbackLink = document.getElementById('feedback-link');

    /* Its figures — the Quests count and the Moonlight meter — are held from
       the strip's first paint until both reads have settled, then shown
       together (lobby-arrival.js holdAccountFigures). Released below. */
    let accountFigures = null;
    if (userControls) {
        userControls.style.display = 'flex';
        accountFigures = holdAccountFigures(userControls);
        /* RIDE THE SAME FADE AS THE VIEW BEHIND IT.
           `#user-controls` is `position: fixed`, so it sits outside
           `#lobby-view` and the view-enter fade never touched it: the strip
           snapped to full opacity while the lobby was still at opacity 0.
           Measured on a real warm reload (real-chrome, dev backend): Stardust
           and the username painted at 806ms with `#lobby-view` at
           `display: block`, a full 1400x618 box and `opacity: 0`. Same class,
           same 200ms curve, so the whole screen arrives as one crossfade —
           which is what "we need to load everything at once, it should look
           polished" asks for. `.view-enter` is unscoped and self-removing, and
           it opts out under `prefers-reduced-motion`. */
        Effects?.viewEnter?.(userControls);
    }

    // Show Stardust
    if (stardustEl) {
        stardustEl.style.display = 'inline-flex';
        /* MOUNTED, not painted once. Every Stardust spend happens on another
           surface (Collection packs, Foundry crafts, Moon Dream), and each of
           those kept its own figure current while this chip stayed at the
           sign-in balance until a reload. The surfaces now announce the
           authoritative balance on `moonfall:stardust-changed` and the chip
           follows; an announcement without a figure re-reads the profile. */
        stardustChip?.dispose?.();
        /* The number alone: the Stardust glyph leads it from CSS
           (`.user-stardust::before`), the same drawing the store's balance
           pill and pack prices carry, so the chip and the store it opens
           agree in every language. */
        stardustChip = mountStardustChip(stardustEl, {
            initial: convexUser?.stardust || 0,
            render: Stardust?.format || ((value) => `${value}`),
            read: async () => (await MoonfallCloud?.users?.getProfile?.())?.stardust,
        });
        /* The Store, the Exchange, the Collection and the Foundry are one tap
           away from here: the lobby warms what each opens with once the boot
           veil lifts — one page or one small read each, never a whole list —
           and only while the lobby is still the screen when each warm runs,
           so a reload into a match reads nothing for a lobby it never shows
           (Lobby.warmAtIdle). */
        void Lobby?.warmAtIdle?.();
    }

    /* Moonlight. Best-effort and non-blocking: the chip renders 'unknown'
       rather than a dark moon if the read fails, and NOTHING about play waits
       on it (docs/asset-loading.md law 3) — only the boot veil's capped
       lobby-arrival hold does, so the value is printed before the lobby shows. */
    let moonlight = null;
    const moonlightEl = document.getElementById('user-moonlight');
    if (moonlightEl) {
        /* THE CHIP HAS TO ANSWER. It used to paint a value and do nothing —
           no hover, no click, no way to learn what it meant.

           HOVER peeks: the expanded card is the only surface where a duel
           count appears, because the count varies ~2x with PvP and ~2.5x with
           manga and no single number is honest on the chip itself.

           CLICK opens the Moon Store, exactly like the Stardust chip beside
           it — the owner's point was that Stardust is clearer *because* it
           goes somewhere. A touch screen has no hover, so a tap goes straight
           to the store, which shows the same meter and the same counts. */
        /* MOUNTED, not read once. A Convex read taken before the auth token is
           attached is anonymous and answers `{ state: 'unknown' }`, which the
           chip renders as an em-dash. Losing that race used to last the whole
           session — the mount re-reads when the identity lands. */
        moonlight = mountMoonlightChip(moonlightEl, {
            read: async ({ force }) => {
                if (force) MoonfallCloud?.users?.forgetMoonlightStatus?.();
                const status = await MoonfallCloud?.users?.getMoonlightStatus?.();
                /* On hold for a disputed payment (2026-09-28): explained once
                   per visit, never stacked over another dialog. */
                try {
                    explainAccountOnHoldOnce(status, { t: resolvedText, anchor: moonlightEl });
                } catch (error) {
                    console.warn('[Main] Could not explain the account hold:', error);
                }
                return status;
            },
            /* Through `resolvedText`, which treats a returned key as missing:
               `t(...) || fallback` named the chip "moonlight.aria" whenever
               the read beat ui.json. The chip repaints this when the
               translations arrive and when the language changes. */
            label: (status) => {
                const percent = Math.max(0, Math.round(Number(status?.percent) || 0));
                return resolvedText('moonlight.aria', `Moonlight, ${percent} percent`, { n: percent });
            },
        });

        // Checkout return retries also refresh while its webhook is arriving.
        window.addEventListener('moonfall:moonlight-stale', () => {
            try { moonlight.refresh(); } catch (_) { /* never break the lobby */ }
        });

        const showCard = () => {
            const status = moonlight.getStatus();
            // The card keeps its own English for any key this cannot resolve yet.
            openMoonlightCard(moonlightEl, status, {
                t: (key, replacements) => i18n?.t?.(key, replacements),
                // On hold for a disputed payment: the card says why it reads free.
                ...(status?.paymentDisputeOnHold === true
                    ? { note: accountOnHoldCopy(status, resolvedText).line }
                    : {}),
            });
        };
        moonlightEl.addEventListener('mouseenter', showCard);
        moonlightEl.addEventListener('mouseleave', closeMoonlightCard);
        moonlightEl.addEventListener('focus', onKeyboardFocus(moonlightEl, showCard));
        moonlightEl.addEventListener('blur', closeMoonlightCard);
        /* ON A PHONE THE CHIP EXPLAINS ITSELF; IT IS NOT A DOOR.

           The owner: "on mobile it's impossible to see the duel count
           approximations. make it such that taping moonlight bar never
           accessed store on mobile. that remains reserved for stardust sign.
           pressing moonlight just creates that popup with the approximations.
           desktop remains unchanged."

           A touch screen has no hover, so the expanded card — the ONLY place a
           duel count is ever printed — had no way of being reached at all: the
           tap went straight past it into the store. Stardust keeps the store,
           which is what the owner reserved it for. */
        /* `Platform` reaches this module untyped, so the call is narrowed here
           rather than adding another entry to the client typecheck ratchet. */
        const onPhoneViewport = () => (
            /** @type {{ isMobileViewport?: () => boolean }} */ (Platform)
        )?.isMobileViewport?.() === true;
        moonlightEl.addEventListener('click', (event) => {
            /* THE "+" IS THE STORE DOOR ON EVERY DEVICE, with its own beat.
               The rest of the chip explains on a phone (the card) and opens
               the store on a desktop, as before. */
            const onPlus = isPlusPress(moonlightEl, event);
            if (onPlus) pulsePlus(moonlightEl);
            if (onPhoneViewport() && !onPlus) {
                event.preventDefault();
                /* The synthesized mouse sequence a tap produces opens the card
                   on `mouseenter` and then closes it again on `mousedown`,
                   because the card dismisses on any pointer press. Re-opening
                   here is what survives that sequence. */
                showCard();
                return;
            }
            closeMoonlightCard();
            /* The chip's "+" leads to the refill: the store lands on it (and
               lights it) for a pass holder, and just opens for anyone else. */
            Store?.open?.({ returnFocus: moonlightEl, spotlight: 'refill' });
        });

        /* Stardust gets the same treatment, deliberately much lighter: it
           needs no explaining, so the tip is one word. It already opens the
           store on click. */
        const stardustEl = document.getElementById('user-stardust');
        if (stardustEl) {
            const showTip = () => openStardustTip(stardustEl, {
                t: (key) => i18n?.t?.(key),
            });
            stardustEl.addEventListener('mouseenter', showTip);
            stardustEl.addEventListener('mouseleave', closeMoonlightCard);
            stardustEl.addEventListener('focus', onKeyboardFocus(stardustEl, showTip));
            stardustEl.addEventListener('blur', closeMoonlightCard);
            stardustEl.addEventListener('click', closeMoonlightCard);
        }

    }

    /* THIS BROWSER HAS A MOONFALL ACCOUNT — remember it.
       Read once, at boot, by `handleSignedOutBoot` to tell a genuinely
       anonymous first visit apart from a signed-in player whose session the
       SDK has not finished restoring. See `browserHasSignedIn` below. */
    rememberBrowserHasSignedIn();

    // Show username
    if (userIdentityEl) {
        userIdentityEl.textContent = resolveLobbyUsername(firebaseUser, convexUser);
        userIdentityEl.style.display = 'inline-flex';
        setupAccountMenuHandler(userIdentityEl);
        // The avatar disc beside the name: the chosen summoner's portrait,
        // or the moon until one is chosen. Its box is reserved from the
        // first paint; only the picture arrives here.
        AvatarPicker?.mount?.(convexUser);
    }

    if (discordLink) discordLink.style.display = 'inline-flex';
    if (feedbackLink) feedbackLink.style.display = 'inline-flex';
    /* Quests needs no reveal here. It is account-scoped by CONTAINMENT — it
       lives inside `#user-controls`, switched on a few lines above — and
       phone-scoped by the cascade (`lobby.css` hides it, the phone gate in
       `mobile.css` promotes it). It used to carry an inline `display: none`
       that this block cleared, and that clear was never observed to run: on a
       667x275 boot the button's style attribute is never mutated once while
       Discord and Feedback both flip at ~600ms, so the phone's only route to
       Quests stayed dark. The inline style is gone with it.
       Contract: tests/unit/ui/questsButtonAuthGate.test.js. */

    // Sign Out is offered from the username dropdown only — there is no
    // standalone top-bar button. Publish the action so the menu can call it
    // without the auth path being duplicated or driven by a synthetic click.
    // MUTATED, never reassigned. `app-services.ts` was handed this object at
    // module scope so `ui/account-menu.js` can reach the sign-out action without
    // importing main.js; replacing the binding here would leave the registry
    // holding the empty object from line 51, and the dropdown's Sign Out would
    // silently do nothing.
    Object.assign(Account, {
        /* "Redeem a code" in the username dropdown — the one place a voucher
           is typed. Published here for the same reason Sign Out is. */
        redeemVoucher: () => {
            void VoucherRedeem.open({ source: 'menu', returnFocus: userIdentityEl });
        },
        signOut: async () => {
            closeReferralsForAuthTransition();
            // An explicit sign-out hands the browser back to the play-first
            // funnel; only a session the SDK lost keeps the hint.
            forgetBrowserHasSignedIn();
            // Nor does it start this account's avatar face at the next boot.
            AvatarPicker?.forget?.();
            await Firebase.signOut();
            // Hide lobby and show auth
            lobbyView.style.display = 'none';
            document.body.classList.remove('lobby-active');
            const authView = document.getElementById('auth-view');
            if (authView) {
                authView.style.display = 'flex';
            }
            window.location.reload();
        },
    });

    // Initialize all UI modules
    Lobby.init();
    Referrals?.init?.();
    DiscordPings?.init?.();
    if (Lobby?.updateTutorialButtonMode) {
        Lobby.updateTutorialButtonMode();
    }
    if (Quests?.init) {
        Quests.init();
    }
    if (SpellAnnounce?.init) {
        SpellAnnounce.init();
    }

    /* THE LOBBY ARRIVES WHOLE. The veil is the organized loading screen; it
       now stays until the quest rail, the Moonlight value, the avatar's face,
       the hub paintings, the fonts and the player's language are final, so the
       lobby appears in one frame instead of assembling itself in front of the
       player. Capped, and every signal settles on failure too
       (ui/lobby-arrival.js). Registered BEFORE the reveal below. */
    holdVeilForLobby({
        waitFor: {
            starterCards: starterCardsReady,
            quests: Quests?.whenReady?.(),
            moonlight: moonlight?.ready,
            avatar: AvatarPicker?.whenPainted?.(),
            i18n: i18n?.init?.(),
        },
    });
    /* The bar's two figures show together, once both of their reads have
       settled — on the sign-in path too, where no veil covers the lobby. */
    accountFigures?.when([Quests?.whenReady?.(), moonlight?.ready]);

    // Load user's saved decks for deck selectors
    if (Lobby.loadDecks) {
        Lobby.loadDecks();
    }

    /* A RETURN FROM CHECKOUT GOES BACK TO THE SURFACE IT LEFT FROM, UNDER
       THE VEIL. It used to lift onto the whole lobby, which the restored
       Collection or Foundry then covered 0.3-11 s later: one screen shown,
       then replaced. Held for the restore below, capped (confirm wait plus
       the surface's own presentation), under the veil's backstop. */
    const settleCheckoutReturn = Store?.hasPendingCheckoutReturn?.(firebaseUser?.uid)
        ? BootVeil.hold('checkout-return', Store.CHECKOUT_RETURN_HOLD_MS || 8000)
        : null;

    // Initialize socket connection and event handling
    try {
        SocketHandler.init();
    } finally {
        /* AFTER the socket init: a mid-match refresh registers its resume
           hold in there, and the veil then waits for the board instead of
           flashing the lobby underneath it for a frame or two. In a `finally`
           so a socket that fails to initialise still shows the lobby it
           would have shown before the veil learned to wait. */
        BootVeil.reveal('lobby');
    }

    // Stripe navigates away from Moonfall, so a successful or cancelled
    // checkout returns through a full authenticated bootstrap. Restore a
    // one-shot Collection/Foundry destination only after consent and Lobby
    // initialization have completed; invalid or stale hints stay in Lobby.
    if (Store?.restoreCheckoutReturn) {
        try {
            await Store.restoreCheckoutReturn(firebaseUser?.uid);
        } catch (error) {
            console.warn('[Main] Failed to restore the pre-checkout surface:', error);
        } finally {
            settleCheckoutReturn?.();
        }
    } else {
        settleCheckoutReturn?.();
    }

    // The server grant happens during account creation; its presentation stays
    // pending until the lobby is genuinely available. Invitation URLs are
    // deliberately left pending by StarterReward until their match flow ends.
    /* THE ARRIVAL CEREMONY IS A SEQUENCE, NOT A BROADCAST.
       An invited player is owed three beats — the starter grant, then the
       invitation brief that states the matches its reward is conditional on,
       then the payout when it lands. Firing them independently from here put
       two overlays on the same frame; each screen now hands off to the next as
       it CLOSES (see starter-reward.js), so this only has to start the chain
       and pick up the brief when no grant was waiting.

       The brief asks the server whether this account was actually invited, so
       it can never claim a referral that does not exist, and any failure leaves
       the lobby exactly as it was. */
    /* A VOUCHER LINK'S TICKET COMES BACK IN THE LOBBY. A visitor who arrived
       on `/play?redeem=` without an account was shown the ticket, sent to sign
       in, and now has one: the code waited in this tab, and the ticket is
       queued behind the arrival chain so it never lands on the welcome grant.
       A `?room=` arrival leaves it pending, like every other lobby ceremony. */
    if (MoonfallVoucherLink?.getPendingCode?.() && !getPendingRoomInvite()) {
        ArrivalCeremony.queue(() => Promise.resolve(VoucherTicket?.showPendingInLobby?.())
            .catch((err) => { console.warn('[Main] Voucher ticket failed to show:', err); }));
    }

    const consumeLobbyRewards = Referrals?.consumeLobbyRewards
        || StarterReward?.consumeAndShow;
    Promise.resolve(consumeLobbyRewards ? consumeLobbyRewards() : false)
        .then((shown) => (shown ? false : InviteWelcome?.checkAndShow?.()))
        .catch((err) => {
            console.warn('[Main] Failed to show lobby rewards:', err);
        })
        // The chain is under way. Whatever was queued behind it runs once no
        // screen is holding — immediately for a player who was shown none.
        .finally(() => ArrivalCeremony.begin());

    // Set up global game event listeners
    setupGlobalEventListeners();
    if (MoonfallQaEntry?.startIfRequested) {
        await MoonfallQaEntry.startIfRequested({
            href: window.location.href,
            startSoloGame: SocketHandler.startSoloGame.bind(SocketHandler)
        });
    }
    if (window.CLIENT_DEBUG_MODE) console.log('Moonfall application initialized');
}

/**
 * Set up global event listeners that are not tied to specific modules
 */
function setupGlobalEventListeners() {
    if (globalEventListenersBound) {
        return;
    }
    globalEventListenersBound = true;

    // End turn button
    document.getElementById('end-turn-btn')?.addEventListener('click', () => {
        SocketHandler.endTurn();
    });

    // Lobby owns the game/lobby transition. Binding here as well as during
    // Lobby.init() keeps the control available to the signed-out tutorial;
    // the Lobby method is idempotent.
    Lobby?.bindInGameReturnControl?.();

    // The Undo control was removed from the frontend entirely (designer
    // decision, 2026-07-29). The server-side undo capability is untouched.

    /** A dev control's refusal, said in the game's own passing toast. */
    function showDebugRefusal(message) {
        if (!message) return;
        const notification = document.createElement('div');
        notification.className = 'game-notification';
        notification.setAttribute('role', 'status');
        notification.textContent = message;
        document.body.appendChild(notification);
        setTimeout(() => {
            notification.classList.add('fade-out');
            setTimeout(() => notification.remove(), 500);
        }, 2600);
    }

    // Debug: End Match / Debug: Win Match — dev-only shortcuts to reach the
    // end-of-match / rematch screen without playing a full game: the first
    // concedes (defeat screen), the second wins (victory screen). Hidden unless
    // the server enabled CLIENT_DEBUG_MODE (DEBUG_MODE=true, never in
    // production). The server independently re-checks DEBUG_MODE, so this is
    // convenience, not authority.
    // Debug: Reveal Enemy Hand — the third dev row: the opponent's hand face-up
    // for this player, through the engine's own reveal flag, so a revealed
    // card's rendering and hover can be tried by hand.
    // Debug: Enemy Board + Reveal — the fourth: the scene a revealed enemy card
    // is read in, in one press (the AI's own Characters on its board, one of
    // its hand cards face-up). It only works on your own turn in a Solo
    // Adventure, so a refusal says so instead of doing nothing.
    /** @type {Array<{ id: string, run: () => Promise<any>, refusal?: string }>} */
    const debugControls = [
        { id: 'debug-end-match-btn', run: () => SocketHandler.debugEndMatch('concede') },
        { id: 'debug-win-match-btn', run: () => SocketHandler.debugEndMatch('win') },
        { id: 'debug-reveal-hand-btn', run: () => SocketHandler.debugRevealHand() },
        {
            id: 'debug-board-reveal-btn',
            run: () => SocketHandler.debugBoardReveal(),
            refusal: 'game.debugBoardRevealRefused',
        },
    ];
    for (const { id, run, refusal } of debugControls) {
        /** @type {HTMLButtonElement | null} */
        const debugButton = /** @type {any} */ (document.getElementById(id));
        if (!debugButton) continue;
        if (window.CLIENT_DEBUG_MODE === true) {
            debugButton.style.display = '';
            debugButton.addEventListener('click', async () => {
                debugButton.disabled = true;
                try {
                    const result = await run();
                    if (refusal && result?.ok !== true) showDebugRefusal(i18n.t(refusal));
                } finally {
                    debugButton.disabled = false;
                }
            });
        } else {
            debugButton.style.display = 'none';
        }
    }

    // Global dragover listener for deep debugging (disabled by default)
    const DEBUG_DRAG = window.CLIENT_DEBUG_MODE; // Set to true if you really need deep drag tracing
    if (DEBUG_DRAG) {
        document.body.addEventListener('dragover', (event) => {
            console.log('[GLOBAL DRAGOVER] event.target:', event.target);
            const elemAtPoint = document.elementFromPoint(event.clientX, event.clientY);
            console.log('[GLOBAL DRAGOVER] document.elementFromPoint():', elemAtPoint);
            // DO NOT preventDefault() or stopPropagation() here, just observe
        });
    }
}

// Initialize the application when the DOM is fully loaded
// We also need to wait for the Convex module to load (ES modules are async)
document.addEventListener('DOMContentLoaded', () => {
    /*
     * A SHARED MOON EXCHANGE LISTING OPENS ITSELF.
     *
     * Everything about which reader this is, and therefore which action they
     * are offered, lives in `exchange-listing-arrival.js`. What stays here is
     * only what the arrival cannot know: how this app reveals its sign-in view,
     * and how it opens the Exchange. Both are the app's OWN controls -- an
     * earlier version reached for `window.UI`, which exists only in the
     * test-bridge bundle, so the button did nothing at all in the shipped one.
     *
     * Inside DOMContentLoaded rather than at module top level, so importing
     * this module in a test has no side effect -- doing this on import broke 59
     * of them. The id check inside is synchronous and first, so an ordinary
     * `/play` visit does nothing.
     */
    startExchangeListingArrival({
        /* THE AUTHORITATIVE "no session" SIGNAL, not a stopwatch.
           `onAuthStateChanged` fires with `null` for a reader who has none, and
           that is the only honest moment to offer them an account. The arrival
           used to guess with a 3.5s timer, and on a slow boot the guess expired
           before a signed-in reader's own bar had painted -- so they were told
           to create an account and then watched it swap to Buy. */
        subscribeAuth: (callback) => Firebase.subscribe(callback),
        showSignIn: () => {
            /* `showFirebaseSignIn()` is how the app actually reveals sign-in.
               Clicking `#google-signin-btn` did nothing at all -- measured:
               `#auth-view` stayed `display: none` before AND after the click,
               with no error -- because that button lives INSIDE the hidden
               view. Reported as "create an account to buy button doesnt
               work". */
            /* The guest tutorial is running behind the listing, and its mulligan
               overlay sits at 9700 while `#auth-view` sits at 1000 -- so sign-in
               appeared and a click could not reach it (measured:
               `elementFromPoint` on the Google button returned a mulligan card).
               Marked on the body so the raise is scoped to THIS path and global
               auth stacking is untouched, and cleared again the moment the view
               is dismissed -- a permanent raise would leave `#auth-view` above
               everything for the rest of the session. */
            document.body.classList.add('exchange-signin-open');
            /* AND RELEASE IT THE MOMENT AUTH RESOLVES. `proceedToLobby` clears
               it too, but that is several steps away — a brand-new account
               meets the consent gate first, and a raise that outlives the
               sign-in it was for is a raise sitting over whatever comes next. */
            let releaseRaise = () => {};
            releaseRaise = Firebase.subscribe((user) => {
                if (!user) return;
                document.body.classList.remove('exchange-signin-open');
                releaseRaise();
            }) || releaseRaise;
            showFirebaseSignIn();
        },
        /*
         * THE REAL API, NOT TWO SYNTHESIZED CLICKS.
         *
         * This used to be `getElementById('open-collection-btn').click()`
         * followed by the same on the Exchange tab. Both are dropped whenever
         * they land before `Lobby` has bound its handler, and `Collection.open`
         * then returns false without a word — so Browse silently did nothing,
         * non-deterministically, which is the "button that is not a button"
         * defect this whole screen was rebuilt to remove. `Collection` is an
         * imported module: it exists as soon as the bundle runs, and `open`
         * takes the section directly, so neither the binding race nor the
         * second click exists any more.
         */
        openExchange: () => { void Collection.open('exchange'); },
    });

    /*
     * THE REFERRAL REWARD'S BUTTON, ANSWERED HERE.
     *
     * A settled referral pays card packs, and its ceremony button used to say
     * "Claim in Lobby" while doing nothing but closing itself — the player was
     * told to claim and given nowhere to do it. The ceremony now asks for the
     * packs screen and this is the only place that can honour it: `Collection`
     * is imported statically here, whereas `collection.js` already imports
     * `starter-reward.js`, so the ceremony cannot reach it directly without a
     * cycle. Same shape as `openExchange` above — the real API, not a
     * synthesized click on a button that may not be bound yet.
     */
    window.addEventListener('moonfall:open-card-packs', () => {
        void Collection.open('packs');
    });

    // Check if ConvexHttpClient is already available
    if (window.ConvexHttpClient) {
        initializeApp();
    } else {
        // Wait for the convex-ready event from the ES module. When the fallback
        // below has already booted the app, the client is initialized here
        // instead of being dropped — the boot is one-shot, the cloud is not.
        window.addEventListener('convex-ready', () => {
            if (window._moonfallInitialized) initializeCloudClient();
            else initializeApp();
        }, { once: true });

        // Fallback: if Convex doesn't load in 3 seconds, proceed anyway (allows offline dev)
        setTimeout(() => {
            if (!window._moonfallInitialized) {
                console.warn('[Main] Convex did not load in time, proceeding without cloud features');
                initializeApp();
            }
        }, 3000);
    }
});

/**
 * Setup GDPR account context menu
 */
function setupAccountMenuHandler(element) {
    if (!element || !AccountMenu) return;
    AccountMenu.attach(element);
}

// Bridge used by the guest-tutorial module (guest-session.js) to reach the
// sign-in card wiring that lives here. The name is historical: it once avoided a
// collision with `window.MoonfallAuth`, a global that turns out to be assigned
// nowhere in this repository — see the comment in ui/foundry.js `init()`.
const MoonfallSignIn = {
    showFirebaseSignIn,
    showGuestSignInMoment,
    holdVeilForGuestConnection,
};

export { Account };

export { MoonfallSignIn };

export { consumeAndShowCloudAchievementBanners };

/* Publish downwards through a LEAF instead of being imported.
   account-menu, moon-exchange and guest-session each need one of these, each
   only inside a function body behind `?.`, and each is reached from
   `ui/cards.js`. Importing main.js to get them dragged the whole application
   into every page that reuses the card renderer — the offline marketing
   renderer, the self-play dashboard and the Card Admin Plane, none of which
   have Firebase or Convex. See public/scripts/app-services.ts.

   Registering at module scope is safe here and only here: main.js is a page
   ENTRY, so nothing imports it back, and on the game page it is evaluated
   before any of the three call sites can fire. */
setAccountActions(Account);
setSignInPresenter(MoonfallSignIn);
setCloudAchievementBannerConsumer(consumeAndShowCloudAchievementBanners);
